import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import BigNumber from "bignumber.js"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { NumberFormatValues } from "react-number-format"

import { appSupplyUSDSNoticePopupState, appSupplyWithdrawPopupState } from "@/atoms/appPopupState"
import { modalAmountAtom, selectedReserveAtom } from "@/atoms/modal"
import { selectedMainObligationAtom } from "@/atoms/obligations"
import { currentSlotAtom, rateLimiterAtom } from "@/atoms/pools"
import { networkAtom } from "@/atoms/settings"
import { walletAssetsAtom } from "@/atoms/wallet"
import { LiquidationProgress } from "@/components/liquidationProgress"
import { RelendModal } from "@/components/relendModal"
import EmptyRelendModalBody from "@/components/relendModal/emptyRelendModalBody"
import useTrans from "@/hooks/useTrans"
import { DEFAULT_DECIMALS, DEPOSIT, IS_MAINNET, RENEC, WITHDRAW } from "@/utils/constants"
import { HelperText, SelectItem, TransactionDetail } from "@/utils/dataTypes"
import { Box, Tab, Tabs, Typography } from "@mui/material"

import { appModalDisableState } from "@/atoms/appModalDisableState"
import { selectedPoolAtom } from "@/atoms/shared"
import { selectedSolendObligationAtom } from "@/atoms/solendObligations"
import { supplyToEarnAPYAtom, supplyToEarnRewardAtom } from "@/atoms/supplyToEarnState"
import Button from "@/components/base/Button"
import { ModalTxResult } from "@/components/modalTxResult"
import useActionOnChain from "@/hooks/useActionOnchain"
import useCheckingOnChain from "@/hooks/useCheckingOnChain"
import useLocalStorage from "@/hooks/useLocalStorage"
import { useTransactionHandler } from "@/hooks/useTransactionHandler"
import useWalletConnect from "@/hooks/useWalletConnect"
import { supplyConfigs } from "@/utils/actions/supply"
import { withdrawConfigs } from "@/utils/actions/withdraw"
import { replaceLocaleStringWithValues } from "@/utils/helpers"
import { getTokenLogo } from "@/utils/logo"
import { RplToken } from "@/utils/modals/RplToken"
import { sendEventLog } from "@/utils/tracking-events"
import {
  WHITELIST_SYMBOL_SUPPLY_ACTIONS,
  WHITELIST_SYMBOL_WITHDRAW_ACTIONS,
  isSymbolWhitelisted,
} from "@/utils/whitelists"
import { ActionEnum, BlockchainNetwork, SOL, USDS } from "@renec-foundation/relend-adapter"
import { PublicKey } from "@solana/web3.js"
import { ModalInput } from ".."
import { MODAL_ABORT_CONTROLLER_MESSAGE, MODAL_ABORT_CONTROLLER_TIMEOUT, MODEL_DISABLE_TIMEOUT } from "../constants"
import SupplyWithdrawModelStat from "./SupplyWithdrawModalStat"
import * as styles from "./styles"

const View = () => {
  const trans = useTrans()
  const { address, getBalanceNativeToken, isRenecChain, isSolanaChain } = useWalletConnect()
  const [loading, setLoading] = useState<boolean | undefined>(false)
  const [helperText, setHelperText] = useState<HelperText>({
    content: "",
    color: "",
  })
  const [transactionDetail, setTransactionDetail] = useState<TransactionDetail>({
    isError: true,
    isActive: false,
    ctaButtonLink: "",
    title: "",
    content: "",
  })
  const [selectedReserveBalance, setSelectedReserveBalance] = useState<number | undefined>(1)

  const [trackingUTM] = useLocalStorage("trackingUTM", "{}")

  // progress bar
  const [progressWidth, setProgressWidth] = useState(0)

  // atoms
  const [modalDisableState, setModalDisableState] = useAtom(appModalDisableState)
  const [amount, setAmount] = useAtom(modalAmountAtom)
  const [popupState, setPopupState] = useAtom(appSupplyWithdrawPopupState)
  const [tab, setTab] = useState(popupState.type)
  const [selectedReserve, setSelectedReserve] = useAtom(selectedReserveAtom)
  const [walletAssets] = useAtom(walletAssetsAtom)
  const [selectedObligation] = useAtom(selectedMainObligationAtom)
  const [selectedSolendObligation] = useAtom(selectedSolendObligationAtom)
  const [selectedPool] = useAtom(selectedPoolAtom)
  const [rateLimiter] = useAtom(rateLimiterAtom)
  const currentSlot = useAtomValue(currentSlotAtom)
  const supplyToEarnApys = useAtomValue(supplyToEarnAPYAtom)
  const supplyToEarnRewards = useAtomValue(supplyToEarnRewardAtom)
  const setSupplyUSDSNoticePopupState = useSetAtom(appSupplyUSDSNoticePopupState)

  const hasShownUSDSPopupRef = useRef(false)

  // abort controller to control open and close model actions
  const [abortController, setAbortController] = useState(new AbortController())

  const isDepositing = popupState.type === DEPOSIT
  const isWithdrawing = popupState.type === WITHDRAW
  const hasZeroBalance = !selectedReserveBalance || selectedReserveBalance === 0

  // for switching network
  const network = useAtomValue(networkAtom)

  // liquidation health bar
  const usedObligation = selectedObligation ?? {
    totalSupplyValue: new BigNumber(0),
    totalBorrowValue: new BigNumber(0),
    borrowLimit: new BigNumber(0),
    liquidationThreshold: new BigNumber(0),
    borrowOverSupply: new BigNumber(0),
    borrowLimitOverSupply: new BigNumber(0),
    liquidationThresholdFactor: new BigNumber(0),
    weightedTotalBorrowValue: new BigNumber(0),
    weightedBorrowUtilization: new BigNumber(0),
  }

  const borrowLimitOverSupply = usedObligation.totalSupplyValue.isZero()
    ? new BigNumber(0)
    : usedObligation.borrowLimit.dividedBy(usedObligation.totalSupplyValue)

  const weightedBorrowOverSupply = useMemo(() => {
    if (usedObligation.totalSupplyValue.isZero()) return new BigNumber(0)
    const weightedTotalBorrowValue = usedObligation.weightedTotalBorrowValue
    let totalSupplyValue = usedObligation.totalSupplyValue
    const inputedAmount = amount || 0
    const price = selectedReserve?.price ?? new BigNumber(0)
    const inputedAmountToPriceUsd = price.multipliedBy(inputedAmount)

    if (tab === DEPOSIT) totalSupplyValue = totalSupplyValue.plus(inputedAmountToPriceUsd)
    if (tab === WITHDRAW) totalSupplyValue = totalSupplyValue.minus(inputedAmountToPriceUsd)

    const value = weightedTotalBorrowValue.dividedBy(totalSupplyValue)
    if (value.gte(1)) return new BigNumber(1)
    if (value.lte(0)) return new BigNumber(0)

    return value
  }, [tab, amount, usedObligation.totalSupplyValue, usedObligation.weightedTotalBorrowValue, selectedReserve])

  const liquidationThresholdOverSupply = usedObligation.totalSupplyValue.isZero()
    ? new BigNumber(0)
    : usedObligation.liquidationThreshold.dividedBy(usedObligation.totalSupplyValue)

  // hooks
  const { actionHandler } = useActionOnChain()
  const { getTokenAccountInfo } = useCheckingOnChain()
  const { handleTransactionResult } = useTransactionHandler()

  // configs
  const supplyMax = useMemo(
    () => (selectedReserve ? supplyConfigs.calculateMax(network, selectedReserve, walletAssets) : BigNumber(0)),
    [network, selectedReserve, walletAssets],
  )
  const withdrawMax = useMemo(
    () =>
      selectedReserve
        ? selectedReserve.symbol === USDS
          ? withdrawConfigs.calculateMax(
              selectedReserve,
              walletAssets,
              selectedSolendObligation,
              rateLimiter,
              currentSlot,
            )
          : withdrawConfigs.calculateMax(selectedReserve, walletAssets, selectedObligation, rateLimiter, currentSlot)
        : BigNumber(0),
    [selectedReserve, walletAssets, selectedObligation, rateLimiter, currentSlot, selectedSolendObligation],
  )

  const LIST_SYMBOL_WHITE_LIST = isDepositing ? WHITELIST_SYMBOL_SUPPLY_ACTIONS : WHITELIST_SYMBOL_WITHDRAW_ACTIONS
  const isRenecLiquidity = selectedReserve?.symbol === RENEC && isRenecChain
  const isSolanaLiquidity = selectedReserve?.symbol === SOL && isSolanaChain

  const abortAndSetNewAbortController = useCallback(() => {
    abortController.abort(MODAL_ABORT_CONTROLLER_MESSAGE)
    // Timeout so that the code inside the function called this method can be executed
    setTimeout(() => {
      setAbortController(new AbortController())
    }, MODAL_ABORT_CONTROLLER_TIMEOUT)
  }, [abortController])

  const handleFetchReserveBalance = useCallback(async () => {
    if (address && selectedReserve) {
      const result =
        isRenecLiquidity || isSolanaLiquidity
          ? await getBalanceNativeToken(abortController)
          : (
              await getTokenAccountInfo(
                new PublicKey(address),
                new PublicKey(selectedReserve.mintAddress),
                abortController,
              )
            ).tokenBalance
      setSelectedReserveBalance(result)
    }
  }, [
    address,
    abortController,
    selectedReserve,
    getTokenAccountInfo,
    getBalanceNativeToken,
    isRenecLiquidity,
    isSolanaLiquidity,
  ])

  const handleAmountChange = useCallback(
    (values: NumberFormatValues) => {
      setAmount(values.floatValue)
      setHelperText({ content: "", color: "" })
    },
    [setAmount],
  )

  const handleCloseModal = useCallback(() => {
    if (!loading && !modalDisableState) {
      // Abort the current operation when closing the modal
      abortAndSetNewAbortController()

      // Reset modal state
      setPopupState((prev) => ({
        ...prev,
        isOpen: false,
      }))
      setHelperText({ content: "", color: "" })
      setAmount(0)
      setTransactionDetail({
        isActive: false,
        ctaButtonLink: "",
        title: "",
        content: "",
      })
    }
  }, [loading, modalDisableState, abortAndSetNewAbortController, setAmount, setPopupState])

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newTab: typeof DEPOSIT | typeof WITHDRAW) => {
      // Handle USDS case when switching to Withdraw tab
      if (selectedReserve?.symbol === USDS && newTab === WITHDRAW) {
        sendEventLog("view_supply_usds_notice_popup", {
          isMainnet: IS_MAINNET,
        })
        setSupplyUSDSNoticePopupState(true)
        return
      }

      if (isWithdrawing && newTab === DEPOSIT) {
        sendEventLog("view_supply_popup", {
          isMainnet: IS_MAINNET,
        })
      } else if (isDepositing && newTab === WITHDRAW) {
        sendEventLog("view_withdrawal_popup", {
          isMainnet: IS_MAINNET,
        })
      }
      setTab(newTab)
      setPopupState((prev) => ({
        ...prev,
        type: newTab,
      }))
    },
    [isDepositing, isWithdrawing, selectedReserve, setPopupState, setSupplyUSDSNoticePopupState],
  )

  const disableModal = useCallback(() => {
    setModalDisableState(true)

    setTimeout(() => {
      setModalDisableState(false)
    }, MODEL_DISABLE_TIMEOUT)
  }, [setModalDisableState])

  useEffect(() => {
    setTab(popupState.type)
  }, [popupState.type])

  useEffect(() => {
    handleFetchReserveBalance()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReserve, address])

  useEffect(() => {
    // check if current selectedReserve is in whitelist, if not then select default first whitelisted
    const isSelectedReserveInWhitelist =
      selectedReserve && isSymbolWhitelisted(selectedReserve.symbol, LIST_SYMBOL_WHITE_LIST)

    if (!isSelectedReserveInWhitelist && selectedPool) {
      const defaultReserve = selectedPool.reserves.find((r) => isSymbolWhitelisted(r.symbol, LIST_SYMBOL_WHITE_LIST))
      setSelectedReserve(defaultReserve?.address ?? "")
    }

    // Handle USDS case when selecting it as reserve
    if (selectedReserve?.symbol === USDS && tab === WITHDRAW && !hasShownUSDSPopupRef.current) {
      sendEventLog("view_supply_usds_notice_popup", {
        isMainnet: IS_MAINNET,
      })
      hasShownUSDSPopupRef.current = true
      setSupplyUSDSNoticePopupState(true)
    }
  }, [LIST_SYMBOL_WHITE_LIST, selectedPool, selectedReserve, setSelectedReserve, tab, setSupplyUSDSNoticePopupState])

  // Add this effect to reset the ref when the modal closes
  useEffect(() => {
    if (!popupState.isOpen) {
      hasShownUSDSPopupRef.current = false
    }
  }, [popupState.isOpen])

  const SELECT_ITEMS: SelectItem[] = useMemo(() => {
    if (!selectedPool) return []

    return selectedPool.reserves
      .map((r) => {
        const tokenDisplayName = RplToken.getTokenDisplayName(r.symbol, r.name)
        return {
          value: r.address,
          icon: getTokenLogo(r.symbol, r),
          iconName: tokenDisplayName,
          alt: r.symbol,
          symbol: r.symbol,
        }
      })
      .filter((r) => isSymbolWhitelisted(r.symbol, LIST_SYMBOL_WHITE_LIST))
  }, [selectedPool, LIST_SYMBOL_WHITE_LIST])

  const amountNotNaN = useMemo(() => amount ?? 0, [amount])

  const buttonError = useMemo(() => {
    if (!selectedReserve) return <></>

    return isDepositing
      ? supplyConfigs.verifyAction(
          network,
          new BigNumber(amountNotNaN),
          selectedReserve.symbol === USDS ? selectedSolendObligation : selectedObligation,
          selectedReserve,
          walletAssets,
          trans,
        )
      : withdrawConfigs.verifyAction(
          network,
          new BigNumber(amountNotNaN),
          selectedReserve.symbol === USDS ? selectedSolendObligation : selectedObligation,
          selectedReserve,
          walletAssets,
          rateLimiter,
          trans,
          currentSlot,
        )
  }, [
    amountNotNaN,
    currentSlot,
    isDepositing,
    network,
    rateLimiter,
    selectedObligation,
    selectedReserve,
    selectedSolendObligation,
    trans,
    walletAssets,
  ])

  const zeroBalanceGuideText = useMemo(
    () =>
      hasZeroBalance && isDepositing && selectedReserve
        ? `${trans.dashboard.modal.not_enough_balance} ${selectedReserve?.symbol}`
        : null,
    [hasZeroBalance, isDepositing, selectedReserve, trans],
  )

  const submitButtonContent = useMemo(() => {
    switch (true) {
      case !!zeroBalanceGuideText:
        return zeroBalanceGuideText
      case !!buttonError:
        return buttonError
      case isDepositing:
        return trans.dashboard.modal.supply
      default:
        return trans.dashboard.modal.withdraw
    }
  }, [buttonError, isDepositing, trans, zeroBalanceGuideText])

  const submitHandler = useCallback(async () => {
    if (hasZeroBalance && isDepositing) {
      const link =
        network === BlockchainNetwork.RENEC
          ? trans.dashboard.modal.not_enough_balance_link
          : trans.dashboard.modal.not_enough_balance_solana_token_link(selectedReserve?.mintAddress)
      window.open(link, "_blank")
      return
    }

    if (!selectedReserve) return

    setLoading(true)
    setHelperText({ content: "", color: "" })

    const submitAmountNum = amount ?? 0
    const symbol = selectedReserve.symbol ?? ""
    const roundedAmount = new BigNumber(submitAmountNum)
      .shiftedBy(selectedReserve.decimals ?? DEFAULT_DECIMALS)
      .toFixed(0)
    const result = await actionHandler({
      network,
      action: popupState.type === DEPOSIT ? ActionEnum.DEPOSIT : ActionEnum.WITHDRAW,
      amount: roundedAmount,
      symbol,
      mintAddress: selectedReserve?.mintAddress,
      supplyToEarnApys,
      supplyToEarnRewards,
    })

    await handleTransactionResult({
      result,
      popupState,
      amount: submitAmountNum,
      selectedReserve,
      walletAddress: address,
      obligationAddress:
        selectedReserve.symbol === USDS ? selectedSolendObligation?.address : selectedObligation?.address,
      actionMax: withdrawMax,
      buttonError,
      setTransactionDetail,
      setHelperText,
    })
    disableModal()
    setLoading(false)

    if (popupState.type === DEPOSIT && result.isSucceeded) {
      const dataTracking = JSON.parse(trackingUTM)
      dataTracking.isMainnet = IS_MAINNET
      dataTracking.reserveSymbol = selectedReserve.symbol
      dataTracking.reserveAddress = selectedReserve.address
      dataTracking.walletAddress = address
      dataTracking.volume = roundedAmount
      dataTracking.market = network

      sendEventLog("user_supply_success", dataTracking)
    }
  }, [
    hasZeroBalance,
    isDepositing,
    selectedReserve,
    amount,
    actionHandler,
    network,
    trans,
    popupState,
    supplyToEarnApys,
    supplyToEarnRewards,
    disableModal,
    handleTransactionResult,
    address,
    selectedSolendObligation?.address,
    selectedObligation?.address,
    withdrawMax,
    buttonError,
    trackingUTM,
  ])

  const selectedObligationReserve = useMemo(
    () =>
      selectedReserve &&
      (selectedReserve.symbol === USDS
        ? selectedSolendObligation?.deposits.find((d) => d.symbol === selectedReserve.symbol)
        : selectedObligation?.deposits.find((d) => d.symbol === selectedReserve.symbol)),
    [selectedObligation, selectedReserve, selectedSolendObligation],
  )

  const canDisplayWithdrawModel = useMemo(() => {
    if (!selectedObligationReserve) return false

    return selectedObligationReserve.amount.isGreaterThan(0)
  }, [selectedObligationReserve])

  const isSubmitButtonDisabled = useMemo(
    () => loading || (Boolean(buttonError) && (!hasZeroBalance || !isDepositing)),
    [loading, buttonError, hasZeroBalance, isDepositing],
  )

  const titleDialog = useMemo(() => {
    if (!transactionDetail.isActive) return

    return transactionDetail.isError ? trans.dashboard.modal.oops : trans.dashboard.modal.congrats
  }, [trans, transactionDetail])

  if (!selectedReserve || !selectedPool) {
    return <></>
  }

  return (
    <RelendModal
      title={titleDialog}
      isSmallPopup={transactionDetail.isActive}
      hideButtonClose={transactionDetail.isActive}
      open={popupState.isOpen}
      handleClose={handleCloseModal}
      data-testid='modal-supply-withdraw'
      useDrawerOnMobile
    >
      {transactionDetail.isActive && (
        <ModalTxResult transactionDetail={transactionDetail} handleCloseModal={handleCloseModal} />
      )}
      {!transactionDetail.isActive && (
        <Box>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            TabIndicatorProps={{ sx: { display: "none" } }}
            aria-label='modal-tabs-sypply-widthdraw'
            variant='fullWidth'
            className='bg-white border border-solid border-secondary rounded-lg p-[2px]'
          >
            <Tab
              classes={{ selected: "bg-secondary !text-primary font-semibold rounded-lg" }}
              className='flex-1 py-2 text-sm sm:text-lg font-medium text-neutral-500'
              value={DEPOSIT}
              label={trans.dashboard.modal.supply}
              data-testid='modal-supply-tab'
              disabled={loading}
            />
            <Tab
              classes={{ selected: "bg-secondary !text-primary font-semibold rounded-lg" }}
              className='flex-1 py-2 text-sm sm:text-lg font-medium text-neutral-500'
              value={WITHDRAW}
              label={trans.dashboard.modal.withdraw}
              data-testid='modal-withdraw-tab'
              disabled={loading}
            />
          </Tabs>

          {!canDisplayWithdrawModel && isWithdrawing && (
            <EmptyRelendModalBody
              title={replaceLocaleStringWithValues(trans.dashboard.modal.no_supply_reserve, selectedReserve.symbol)}
            />
          )}

          {(isDepositing || canDisplayWithdrawModel) && (
            <Box className='mt-4 sm:mt-6'>
              <ModalInput
                dataTestId='my-supply-borrow-amount-input'
                value={amount}
                setValue={setAmount}
                onValueChange={handleAmountChange}
                selectItems={SELECT_ITEMS}
                selectedReserve={selectedReserve}
                selectedValue={selectedReserve?.address}
                handleSelectChange={(value) => {
                  setSelectedReserve(value)
                  handleFetchReserveBalance()
                }}
                maxValue={tab === DEPOSIT ? supplyMax : withdrawMax}
                disabled={loading}
              />
              <SupplyWithdrawModelStat selectedReserve={selectedReserve} value={amount} type={popupState.type} />
              {selectedReserve.symbol !== USDS ? (
                <Box mb={3}>
                  <LiquidationProgress
                    currentProgress={weightedBorrowOverSupply.toNumber()}
                    progressWidth={progressWidth}
                    borrowLimit={borrowLimitOverSupply.toNumber()}
                    liquidationThreshold={liquidationThresholdOverSupply.toNumber()}
                    setProgressWidth={setProgressWidth}
                  />
                </Box>
              ) : null}
              {helperText.content && (
                <Typography sx={{ ...styles.helperText, color: helperText.color }}>{helperText.content}</Typography>
              )}
              <Button
                variant='primary'
                fullWidth
                onClick={submitHandler}
                disabled={isSubmitButtonDisabled}
                loading={loading}
                data-testid='button-submit-supply-withdraw-modal'
              >
                {submitButtonContent}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </RelendModal>
  )
}

export default View
